import { CheckCircle2 } from "lucide-react";
import { INDUSTRIES, SERVICE_TYPES, SHIFTS, FACILITIES, RELOCATION, SKILLS } from "../landing/data";
import { KycBadge } from "./KycBadge";
import { PhotoThumb } from "./PhotoThumb";

export const spacedAadhaar = (n) => n.replace(/(\d{4})(?=\d)/g, "$1 ");

export const labelOf = (list, v) => list.find((x) => x.value === v)?.label || v || "—";

const date = (r) => new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
const phone = (key) => (r) => (
  <span className="flex items-center gap-1.5 whitespace-nowrap font-mono">
    {r[key]}
    {r.phone_verified && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" aria-label="OTP verified" />}
  </span>
);
const loc = (r) => <span className="whitespace-nowrap">{r.district}, {r.state}</span>;

const PAY_TYPES = [{ value: "monthly", label: "Monthly" }, { value: "daily", label: "Daily" }];
const VERIFIED = [{ value: "true", label: "OTP verified" }, { value: "false", label: "Not verified" }];
const TERMS_FILTER = { key: "terms_accepted", label: "Terms", options: [{ value: "true", label: "Accepted" }, { value: "false", label: "Never accepted" }] };
const TERMS_COLUMN = {
  key: "terms_accepted_at", label: "Terms",
  render: (r) => (r.terms_accepted_at
    ? <span data-testid={`terms-accepted-${r.reference}`} className="whitespace-nowrap text-emerald-300">Accepted<br /><span className="text-[11px] text-slate-400">{date({ created_at: r.terms_accepted_at })}</span></span>
    : <span data-testid={`terms-missing-${r.reference}`} className="whitespace-nowrap text-red-300">Not accepted</span>),
};
const KYC_STATUSES = [{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }];
const KYC_FILTER = { key: "kyc_status", label: "KYC status", options: KYC_STATUSES };
const KYC_COLUMN = {
  key: "kyc_status", label: "KYC",
  render: (r) => (
    <div className="space-y-1">
      <KycBadge status={r.kyc_status} />
      {r.kyc_reviewed_by && <p data-testid={`kyc-reviewer-${r.reference}`} className="whitespace-nowrap text-[11px] text-slate-400">by {r.kyc_reviewed_by_name || r.kyc_reviewed_by}</p>}
    </div>
  ),
};
const ROLE_COLUMN = { key: "job_role", label: "Job role", render: (r) => <span className="whitespace-nowrap">{r.job_role || "—"}</span> };
const AADHAAR_COLUMN = { key: "aadhaar", label: "Aadhaar", render: (r) => <span data-testid={`aadhaar-masked-${r.reference}`} className="whitespace-nowrap font-mono">{r.aadhaar_full ? spacedAadhaar(r.aadhaar_full) : r.kyc?.aadhaar_last4 ? `XXXX-XXXX-${r.kyc.aadhaar_last4}` : "—"}</span> };
const PHOTO_COLUMN = { key: "photo", label: "Photo", render: (r) => <PhotoThumb fileId={r.kyc?.photo_file_id} testId={`photo-${r.reference}`} /> };
const PAN_COLUMN = { key: "pan", label: "PAN", render: (r) => <span className="whitespace-nowrap font-mono">{!r.kyc ? "—" : r.kyc.has_pan === false ? "No PAN" : r.kyc.pan_number}</span> };
const ADDRESS_COLUMN = { key: "full_address", label: "Address", render: (r) => <span className="block min-w-[200px] max-w-xs text-xs">{r.full_address || "—"}</span> };

export const ADMIN_CONFIG = {
  employer: {
    label: "Employers",
    filters: [
      { key: "industry", label: "Industry", options: INDUSTRIES },
      { key: "service_type", label: "Service", options: SERVICE_TYPES },
      { key: "shift", label: "Shift", options: SHIFTS },
      { key: "pay_type", label: "Pay type", options: PAY_TYPES },
      { key: "verified", label: "Phone", options: VERIFIED },
      TERMS_FILTER,
      KYC_FILTER,
    ],
    columns: [
      { key: "reference", label: "Reference", render: (r) => <span className="whitespace-nowrap font-mono text-orange-300">{r.reference}</span> },
      KYC_COLUMN,
      PHOTO_COLUMN,
      TERMS_COLUMN,
      { key: "created_at", label: "Submitted", render: date },
      { key: "company_name", label: "Company", render: (r) => <span className="font-semibold text-white">{r.company_name}</span> },
      { key: "gst_number", label: "GST", render: (r) => <span className="font-mono">{r.gst_number || "—"}</span> },
      { key: "hr_name", label: "HR contact" },
      { key: "phone", label: "Phone", render: phone("phone") },
      AADHAAR_COLUMN,
      PAN_COLUMN,
      { key: "location", label: "Location", render: loc },
      ADDRESS_COLUMN,
      { key: "industry", label: "Industry", render: (r) => labelOf(INDUSTRIES, r.industry) },
      { key: "service_type", label: "Service", render: (r) => labelOf(SERVICE_TYPES, r.service_type) },
      ROLE_COLUMN,
      { key: "headcount", label: "Heads", render: (r) => <span className="font-mono">{r.headcount}</span> },
      { key: "shift", label: "Shift", render: (r) => labelOf(SHIFTS, r.shift) },
      { key: "pay", label: "Pay", render: (r) => <span className="whitespace-nowrap">₹{r.pay_amount.toLocaleString("en-IN")} / {r.pay_type === "daily" ? "day" : "month"}</span> },
      { key: "facilities", label: "Facilities", render: (r) => r.facilities.map((f) => labelOf(FACILITIES, f)).join(", ") || "—" },
    ],
  },
  worker: {
    label: "Employees",
    filters: [
      { key: "skill_category", label: "Skill", options: SKILLS },
      { key: "relocation", label: "Relocation", options: RELOCATION },
      { key: "verified", label: "Phone", options: VERIFIED },
      TERMS_FILTER,
      KYC_FILTER,
    ],
    columns: [
      { key: "reference", label: "Reference", render: (r) => <span className="whitespace-nowrap font-mono text-orange-300">{r.reference}</span> },
      KYC_COLUMN,
      PHOTO_COLUMN,
      TERMS_COLUMN,
      { key: "created_at", label: "Submitted", render: date },
      { key: "full_name", label: "Name", render: (r) => <span className="font-semibold text-white">{r.full_name}</span> },
      { key: "parents", label: "Father / Mother", render: (r) => <span className="whitespace-nowrap">{r.father_name || "—"}<br /><span className="text-slate-500">{r.mother_name || "—"}</span></span> },
      { key: "mobile", label: "Mobile", render: phone("mobile") },
      AADHAAR_COLUMN,
      PAN_COLUMN,
      { key: "age", label: "DOB / Age", render: (r) => <span className="whitespace-nowrap">{r.date_of_birth || "—"}<br /><span className="text-slate-500">{r.age} yrs</span></span> },
      { key: "emergency", label: "Emergency", render: (r) => <span className="whitespace-nowrap">{r.emergency_contact_name || "—"}<br /><span className="font-mono text-slate-500">{r.emergency_contact_phone || ""}</span></span> },
      { key: "physique", label: "Ht / Wt", render: (r) => <span className="whitespace-nowrap">{r.height_cm ? `${r.height_cm} cm` : "—"} / {r.weight_kg ? `${r.weight_kg} kg` : "—"}</span> },
      { key: "gender", label: "Gender / Marital", render: (r) => <span className="whitespace-nowrap capitalize">{r.gender || "—"}<br /><span className="text-slate-500">{r.marital_status || "—"}</span></span> },
      { key: "location", label: "Location", render: loc },
      ADDRESS_COLUMN,
      { key: "skill_category", label: "Skill", render: (r) => labelOf(SKILLS, r.skill_category) },
      ROLE_COLUMN,
      { key: "relocation", label: "Relocation", render: (r) => labelOf(RELOCATION, r.relocation) },
      { key: "uan_number", label: "UAN", render: (r) => <span className="font-mono">{r.uan_number || "—"}</span> },
      { key: "esic_number", label: "ESIC", render: (r) => <span className="font-mono">{r.esic_number || "—"}</span> },
    ],
  },
};
